import React from 'react';
import { Link } from 'react-router-dom';
import CaseStudyGate from '../../components/CaseStudyGate';
import Footer from '../../components/Footer';
import OtherProjects from '../../components/OtherProjects';
import useNoIndex from '../../hooks/useNoIndex';

// Herba brand green, sampled directly from the pitch deck (team names, card
// labels, the wordmark's leaf accent).
const ACCENT = '#00C853';
// A darkened version of the same green for text/borders on light
// backgrounds — the raw brand green fails AA contrast on white, the same
// problem Disco solved by deriving '#0F5A63' from its brand teal.
const ACCENT_DARK = '#067A34';
// Herba's dark accent-band background, used for the bands lower on the
// page, the same move Disco makes with its teal-gradient "Employee
// nomination experience" panel.
const DARK = '#000000';
// A solid, visibly-lighter-than-black fill for the small "bubble" cards
// inside those dark bands (DarkCard, PivotCard, TimelineStep). These used
// to be a faint white tint over DARK, which read as a subtle gray card
// when DARK was '#1E1E1E' — but the same tint over true black is nearly
// invisible, so the cards need their own explicit dark-gray background
// instead of one computed relative to the page background.
const CARD_BG = '#1E1E1E';

const SectionHeading = ({ eyebrow, title, intro }) => (
  <div className="mb-12">
    <p className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: ACCENT_DARK }}>
      {eyebrow}
    </p>
    <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">{title}</h2>
    {intro && <p className="text-tertiary text-lg max-w-2xl">{intro}</p>}
  </div>
);

// A screenshot presented with light browser chrome, a real shadow, and a caption.
const Shot = ({ src, alt, caption, step, chrome = true }) => (
  <figure className="flex flex-col h-full">
    <div className="rounded-lg overflow-hidden shadow-xl border border-gray-light bg-white">
      {chrome && (
        <div className="flex items-center gap-1.5 px-3 py-2 bg-gray-light">
          <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
        </div>
      )}
      <img src={src} alt={alt} className="w-full h-auto block" />
    </div>
    <figcaption className="mt-3 text-sm text-tertiary">
      {step && (
        <span className="font-bold mr-1.5" style={{ color: ACCENT_DARK }}>
          {step}
        </span>
      )}
      {caption}
    </figcaption>
  </figure>
);

// A screenshot framed as an iPhone rather than a browser window — used for the
// three screens that are actually mobile, not web: setup, detail view, and AI
// monitoring, all captured or used by a crew member in the field. The notch
// and home indicator live in their own dark-bezel padding above/below the
// screenshot rather than sitting on top of it, so real UI (a header, a modal
// title) never ends up hidden under a decorative overlay.
const PhoneShot = ({ src, alt }) => (
  <div className="mx-auto w-full" style={{ maxWidth: 240 }}>
    <div
      className="relative rounded-[2.5rem]"
      style={{
        backgroundColor: '#1a1a1a',
        boxShadow: '0 20px 40px -12px rgba(0,0,0,0.35)',
        padding: '26px 8px 18px',
      }}
    >
      {/* side buttons */}
      <span className="absolute rounded-l" style={{ left: -2, top: 90, width: 3, height: 28, backgroundColor: '#1a1a1a' }} />
      <span className="absolute rounded-l" style={{ left: -2, top: 128, width: 3, height: 46, backgroundColor: '#1a1a1a' }} />
      <span className="absolute rounded-r" style={{ right: -2, top: 108, width: 3, height: 56, backgroundColor: '#1a1a1a' }} />

      {/* notch, in the top bezel padding, above the screenshot */}
      <span
        className="absolute left-1/2 -translate-x-1/2 rounded-full"
        style={{ top: 8, width: 70, height: 16, backgroundColor: '#000' }}
      />

      <div className="rounded-[1.5rem] overflow-hidden bg-white">
        <img src={src} alt={alt} className="w-full h-auto block" />
      </div>

      {/* home indicator, in the bottom bezel padding, below the screenshot */}
      <div className="flex justify-center pt-3">
        <span className="rounded-full" style={{ width: 90, height: 3, backgroundColor: 'rgba(255,255,255,0.5)' }} />
      </div>
    </div>
  </div>
);

// A team or advisor card — real roster from the deck, rebuilt as crisp text
// instead of embedding the low-res slide photos.
// Initials for the monogram avatar, skipping titles like "Dr."
const initials = (name) =>
  name
    .split(' ')
    .filter((part) => !part.endsWith('.'))
    .map((part) => part[0])
    .slice(0, 2)
    .join('');

// Team member card. Founders get the dark pitch-deck treatment so they lead
// the section; advisors sit below on light cards with the same layout.
const TeamCard = ({ name, role, credential, founder }) => (
  <div
    className={`group flex flex-col rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 ${
      founder ? 'shadow-lg hover:shadow-xl' : 'bg-white shadow-sm hover:shadow-lg'
    }`}
    style={founder ? { backgroundColor: DARK } : undefined}
  >
    <div
      className={`rounded-full flex items-center justify-center font-bold mb-5 ${
        founder ? 'w-14 h-14 text-lg' : 'w-11 h-11 text-sm'
      }`}
      style={{
        background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_DARK})`,
        color: '#FFFFFF',
      }}
      aria-hidden="true"
    >
      {initials(name)}
    </div>
    <p
      className="text-[11px] font-bold uppercase tracking-widest mb-2"
      style={{ color: founder ? ACCENT : ACCENT_DARK }}
    >
      {role}
    </p>
    <p className={`font-bold mb-3 ${founder ? 'text-xl text-white' : 'text-lg text-text-primary'}`}>{name}</p>
    <p
      className={`text-sm pt-3 mt-auto border-t ${founder ? 'border-white/15' : 'border-gray-light text-tertiary'}`}
      style={founder ? { color: '#B8B8B8' } : undefined}
    >
      {credential}
    </p>
  </div>
);

// A dark, green-accented card for the "Who uses Herba? / Why?" grids —
// deliberately reusing the actual pitch-deck card style (dark fill, subtle
// border, bold green label) rather than the light case-study card pattern,
// so this one band of the page reads as unmistakably Herba's own brand.
const DarkCard = ({ label, sub, large }) => (
  <div
    className="rounded-lg p-5"
    style={{ backgroundColor: CARD_BG, border: '1px solid rgba(255,255,255,0.1)' }}
  >
    <p className={`font-bold mb-1 ${large ? 'text-2xl' : ''}`} style={{ color: ACCENT }}>
      {label}
    </p>
    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.65)' }}>
      {sub}
    </p>
  </div>
);

// One step in "The pivot" flow, inside the dark band — a gray card for
// context, or a green-highlighted one for the two beats that mattered (the
// research finding and the funding it led to).
const PivotCard = ({ eyebrow, title, highlight, children }) => (
  <div
    className="rounded-lg p-5 flex-1 lg:min-w-[150px]"
    style={
      highlight
        ? { backgroundColor: 'rgba(0,200,83,0.07)', border: `1px solid ${ACCENT}` }
        : { backgroundColor: CARD_BG, border: '1px solid rgba(255,255,255,0.12)' }
    }
  >
    <p
      className="text-xs font-bold uppercase tracking-widest mb-2"
      style={{ color: highlight ? ACCENT : 'rgba(255,255,255,0.4)' }}
    >
      {eyebrow}
    </p>
    <p className="text-white font-bold text-lg mb-2">{title}</p>
    <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.65)' }}>
      {children}
    </p>
  </div>
);

// The connector between pivot cards — a horizontal arrow with a caption on
// desktop, rotated into a vertical one when the row stacks on mobile.
const PivotArrow = ({ caption }) => (
  <div className="flex lg:flex-col items-center justify-center gap-2 lg:gap-1 py-3 lg:py-0 lg:px-3 flex-shrink-0">
    <span className="text-xl leading-none rotate-90 lg:rotate-0" style={{ color: 'rgba(255,255,255,0.4)' }}>
      →
    </span>
    <span
      className="text-[11px] text-center leading-tight lg:max-w-[72px]"
      style={{ color: 'rgba(255,255,255,0.65)' }}
    >
      {caption}
    </span>
  </div>
);

// One step in a vertical dark-band timeline (a dot-and-line connector down
// the left edge, matching PivotCard's gray-vs-highlight card treatment) —
// used to walk through how a raw sensor reading becomes a predicted date.
const TimelineStep = ({ eyebrow, title, highlight, last, children }) => (
  <div className={`relative pl-8 ${last ? '' : 'pb-4'}`}>
    <span
      className="absolute left-0 top-1.5 w-2.5 h-2.5 rounded-full"
      style={{ backgroundColor: ACCENT }}
    />
    {!last && (
      <span
        className="absolute left-[4px] top-4 bottom-0 w-px"
        style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
      />
    )}
    <div
      className="rounded-lg p-4"
      style={
        highlight
          ? { backgroundColor: 'rgba(0,200,83,0.08)', border: `1px solid ${ACCENT}` }
          : { backgroundColor: CARD_BG, border: '1px solid rgba(255,255,255,0.12)' }
      }
    >
      <p
        className="text-[10px] font-bold uppercase tracking-widest mb-1"
        style={{ color: highlight ? ACCENT : 'rgba(255,255,255,0.4)' }}
      >
        {eyebrow}
      </p>
      <p className="text-white font-bold text-sm mb-1">{title}</p>
      <div className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
        {children}
      </div>
    </div>
  </div>
);

// The "what changed next" line inside a TimelineStep card, set off by a
// divider — used for the beta findings, where every problem is paired with
// the production change it drove.
const NextChange = ({ children }) => (
  <div className="flex gap-2.5 mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
    <span
      className="text-[10px] font-bold uppercase tracking-widest flex-shrink-0 pt-0.5"
      style={{ color: ACCENT }}
    >
      Next
    </span>
    <span className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
      {children}
    </span>
  </div>
);

const BeforeAfterPair = ({ before, after, alt }) => (
  <div className="grid grid-cols-2 gap-3">
    <figure>
      <img src={before} alt={`${alt}, before Herba`} className="w-full h-auto rounded-lg object-cover" />
      <figcaption className="text-center text-xs font-bold uppercase tracking-widest text-tertiary mt-2">
        Before
      </figcaption>
    </figure>
    <figure>
      <img src={after} alt={`${alt}, after Herba`} className="w-full h-auto rounded-lg object-cover" />
      <figcaption className="text-center text-xs font-bold uppercase tracking-widest text-tertiary mt-2">
        After
      </figcaption>
    </figure>
  </div>
);

// A self-contained wireframe palette for the "site flow" sitemap diagram —
// deliberately its own muted, cream-and-ink system rather than Herba's
// green/black, the same way Disco's hand-sketch mockups run on their own
// SKETCH_BG/INK palette instead of Disco's real brand teal.
const WIRE = {
  card: '#ECEEF1',
  node: '#56697C',
  border: '#3A3A3A',
  ink: '#2B3A4A',
  onNode: '#FFFFFF',
  onNodeMuted: 'rgba(255, 255, 255, 0.8)',
  field: '#6B6B6B',
  muted: '#8A94A0',
  accent: '#4F7FA0',
  highlight: '#3F6F91',
  warn: '#B3261E',
};

// Injects the org-chart connector-line CSS for the site-flow tree, scoped
// under .herba-tree so it can't leak into the rest of the site — the same
// injected-<style> move Disco's useSketchStyles makes for its own diagram.
const useTreeStyles = () => {
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .herba-tree ul { padding-top: 20px; position: relative; display: flex; justify-content: center; margin: 0; }
      .herba-tree li { list-style: none; position: relative; padding: 20px 10px 0 10px; display: flex; flex-direction: column; align-items: center; }
      .herba-tree li::before, .herba-tree li::after { content: ''; position: absolute; top: 0; right: 50%; border-top: 1px solid #c5cbd2; width: 50%; height: 20px; }
      .herba-tree li::after { right: auto; left: 50%; border-left: 1px solid #c5cbd2; }
      .herba-tree li:only-child::after, .herba-tree li:only-child::before { display: none; }
      .herba-tree li:only-child { padding-top: 0; }
      .herba-tree li:first-child::before, .herba-tree li:last-child::after { border: 0 none; }
      .herba-tree li:last-child::before { border-right: 1px solid #c5cbd2; }
      .herba-tree ul ul::before { content: ''; position: absolute; top: 0; left: 50%; border-left: 1px solid #c5cbd2; width: 0; height: 20px; }
      .herba-tree > ul { padding-top: 0; }
      .herba-tree > ul > li { padding-top: 0; }
      .herba-tree > ul > li::before, .herba-tree > ul > li::after { display: none; }
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);
};

// The mini wireframe "screen" card at each tree node.
const TreeNode = ({ label, children }) => (
  <div
    className="rounded-lg p-2.5 text-left flex-shrink-0"
    style={{ backgroundColor: WIRE.card, border: `1px solid ${WIRE.border}`, width: 168 }}
  >
    <p className="text-[11px] font-bold text-center mb-2" style={{ color: WIRE.ink }}>
      {label}
    </p>
    {children}
  </div>
);

// Placeholder atoms standing in for real UI chrome inside a TreeNode.
const WLine = ({ thin, width, center }) => (
  <div
    className="rounded-sm mb-1"
    style={{
      height: thin ? 4 : 6,
      border: `1px solid ${thin ? WIRE.muted : WIRE.field}`,
      width: width || '100%',
      margin: center ? '0 auto 8px' : undefined,
    }}
  />
);
const WRow = ({ children }) => <div className="flex gap-1 items-center mb-1">{children}</div>;
const WCircle = () => (
  <span
    className="w-3.5 h-3.5 rounded-full flex-shrink-0"
    style={{ border: `1px solid ${WIRE.field}` }}
  />
);
const WPill = ({ maxWidth, thin }) => (
  <span
    className="flex-1 rounded-full"
    style={{ height: 14, border: `1px solid ${thin ? WIRE.muted : WIRE.field}`, maxWidth }}
  />
);
const WStatRow = () => (
  <div className="flex gap-1 mb-1.5">
    {[0, 1, 2].map((i) => (
      <span key={i} className="flex-1 rounded" style={{ height: 22, border: `1px solid ${WIRE.field}` }} />
    ))}
  </div>
);
const WBtnRow = ({ center, children }) => (
  <div className={`flex gap-1 mt-1 ${center ? 'justify-center' : 'justify-end'}`}>{children}</div>
);
const WBtn = ({ width = 30 }) => (
  <span className="rounded-md" style={{ height: 14, width, border: `1px solid ${WIRE.accent}` }} />
);
const WGraph = () => (
  <div className="rounded mb-1 overflow-hidden" style={{ height: 26, border: `1px solid ${WIRE.field}` }}>
    <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="w-full h-full">
      <polyline
        points="0,20 15,10 30,18 45,8 60,16 75,6 90,14 100,10"
        fill="none"
        stroke={WIRE.field}
        strokeWidth="2"
      />
    </svg>
  </div>
);
const WMap = () => (
  <div
    className="rounded mb-1"
    style={{
      height: 26,
      border: `1px solid ${WIRE.field}`,
      backgroundImage: `repeating-linear-gradient(45deg, ${WIRE.card} 0 4px, #dde1e6 4px 8px)`,
    }}
  />
);
const WModal = ({ children }) => (
  <div className="rounded-md p-2" style={{ border: `1px dashed ${WIRE.field}` }}>
    {children}
  </div>
);

// A single step box in the flow chart diagrams: a solid filled box for a
// committed state, an outlined dashed box for either/or branches, `highlight`
// for a step worth calling out, and `end` for the flow's final step.
const FlowBox = ({ label, sub, dashed, highlight, end }) => (
  <div
    className="rounded-lg px-3 py-2.5 text-center"
    style={
      dashed
        ? { border: `1px dashed ${WIRE.node}` }
        : { backgroundColor: end ? WIRE.ink : highlight ? WIRE.highlight : WIRE.node }
    }
  >
    <p className="text-[11px] font-medium leading-snug" style={{ color: dashed ? WIRE.ink : WIRE.onNode }}>
      {label}
    </p>
    {sub && (
      <p className="text-[10px] mt-1 leading-snug" style={{ color: dashed ? WIRE.field : WIRE.onNodeMuted }}>
        {sub}
      </p>
    )}
  </div>
);

// The connector between flow steps.
const FlowArrow = () => (
  <div className="flex justify-center py-1" style={{ color: WIRE.muted }} aria-hidden="true">
    <span className="text-base leading-none">↓</span>
  </div>
);

const TEAM = [
  { name: 'Kacey Fillingim', role: 'CEO & Co-founder', credential: 'Experienced product designer', founder: true },
  { name: 'Anson Cheung', role: 'Co-founder, Industrial Design', credential: 'Industrial designer of Eight Sleep', founder: true },
  { name: 'Sivan Sud', role: 'Advisor, Engineering', credential: 'Cornell EE grad & founder' },
  { name: 'Dr. Robert Flannery', role: 'Advisor, Plant Science', credential: 'PHD in plant biology + CEO of Dr. Robb Farms' },
  { name: 'Ron Storn', role: 'Advisor, Business', credential: 'Former CPO of Lyft' },
  { name: 'Grant Morgan', role: 'Advisor, Engineering & Production', credential: 'Co-founder of R-Zero' },
];

// The reference table of every notification type Herba sends, each one
// following the same "[Device Name] – <issue>" header + plain-language
// subcopy pattern. Only tank_filled is informational; every other type
// is a warning, which the table's dot color surfaces at a glance.
const ALERT_TYPES = [
  {
    type: 'water_percentage',
    severity: 'warning',
    header: '"[Location] [Device Name] - Refill Tank"',
    subcopy: '"Water Level [X]%. Please refill tank to continue watering. [Date Time]"',
  },
  {
    type: 'tank_disconnected',
    severity: 'warning',
    header: '"[Location] [Device Name] - Tank Disconnected"',
    subcopy: '"Water tank is disconnected. Reconnect the tank to resume watering. [Date Time]"',
  },
  {
    type: 'tank_unsealed',
    severity: 'warning',
    header: '"[Location] [Device Name] - Tank Not Sealed"',
    subcopy: '"Water tank lid is not sealed. Reseal to prevent leaks and evaporation. [Date Time]"',
  },
  {
    type: 'tank_filled',
    severity: 'info',
    header: '"[Location] [Device Name] - Tank Refilled"',
    subcopy: '"Tank has been refilled. Watering will resume as scheduled. [Date Time]"',
  },
  {
    type: 'battery_percentage',
    severity: 'warning',
    header: '"[Location] [Device Name] - Low Battery"',
    subcopy: '"Battery at [X]%. Charge or replace the battery soon. [Date Time]"',
  },
  {
    type: 'battery_voltage',
    severity: 'warning',
    header: '"[Location] [Device Name] - Battery Critical"',
    subcopy: '"Battery voltage is critically low. Device may shut down. [Date Time]"',
  },
  {
    type: 'status',
    severity: 'warning',
    header: '"[Location] [Device Name] - Device Offline"',
    subcopy: '"This device has lost connection. Check power and network. [Date Time]"',
  },
];

const Herba = () => {
  useNoIndex();
  useTreeStyles();
  const IMG = '/images/case-studies/herba';

  return (
    <CaseStudyGate projectName="Herba">
      <div className="min-h-screen bg-primary">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="pt-8">
            <Link
              to="/"
              className="inline-flex items-center text-tertiary hover:text-[#00C853] transition-colors duration-300"
            >
              ← Back to Home
            </Link>
          </div>

          {/* Header */}
          <header className="pt-10 pb-16 sm:pt-16 sm:pb-20">
            <img src="/images/herba_logo_color.png" alt="Herba" className="h-10 sm:h-12 object-contain mb-8" />
            <h1 className="text-4xl sm:text-5xl font-bold text-text-primary leading-tight mb-6 max-w-3xl">
              Automating plant care, from a consumer gadget into a funded commercial product for
              landscaping teams.
            </h1>
            <p className="text-tertiary text-sm mb-6"><span className="font-bold">Founder + CEO</span> · June 2023 - October 2025</p>
            <div className="w-16 h-1 mb-8" style={{ backgroundColor: ACCENT_DARK }} />
            <div className="grid grid-cols-[2fr_1fr] gap-6 text-sm max-w-3xl">
              <div>
                <p className="text-tertiary mb-1">Team</p>
                <p className="text-text-primary font-medium">
                  Founder and CEO. Wore every hat (hardware, firmware, product design, and business)
                  to take Herba from idea to a patented, funded, working product. Later built out a
                  team of co-founders and advisors spanning industrial design, engineering, plant
                  science, and business.
                </p>
              </div>
              <div>
                <p className="text-tertiary mb-1">Timeline</p>
                <p className="text-text-primary font-medium">June 2023 - October 2025</p>
              </div>
              <div>
                <p className="text-tertiary mb-1">Goal</p>
                <p className="text-text-primary font-medium">
                  Automate plant care with sensors, a web app, and alerts: first for consumers, then
                  pivoted to solve a hiring and retention problem for commercial landscaping
                  companies.
                </p>
              </div>
              <div>
                <p className="text-tertiary mb-1">Deliverable</p>
                <p className="text-text-primary font-medium">
                  Patented hardware, firmware, and a web/mobile product, from prototype through a
                  funded, beta-tested commercial launch.
                </p>
              </div>
            </div>
          </header>

          {/* Context */}
          <section className="py-16 sm:py-24 border-t border-gray-light">
            <SectionHeading eyebrow="Context" title="A confession that turned into a company" />
            <div className="max-w-3xl space-y-4 text-tertiary text-lg mb-16">
              <p>
                <strong className="text-text-primary">Confession: I kill a lot of plants.</strong> I lost my mother and inherited her plant collection, and found I had no idea how to keep them alive. I couldn't find anything on the market to automate plant care, so I built it.
              </p>
              <p>
                What started as a personal fix for a bad habit became Herba: sensors, a web app, and
                alerts that keep plants alive without anyone having to remember to check on them.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-text-primary mb-3">Putting together the team</h3>
              <p className="text-tertiary text-lg mb-8 max-w-2xl">
                Herba started solo, but scaling it meant assembling a team who'd done this before:
                award-winning product design, hardware engineering, plant science, and go-to-market
                experience from companies like Eight Sleep, Lyft, and R-Zero.
              </p>
              <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-4">Founders</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                {TEAM.filter((m) => m.founder).map((member) => (
                  <TeamCard key={member.name} {...member} />
                ))}
              </div>
              <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-4">Advisors</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {TEAM.filter((m) => !m.founder).map((member) => (
                  <TeamCard key={member.name} {...member} />
                ))}
              </div>
            </div>
          </section>

          {/* Process */}
          <section className="py-16 sm:py-24 border-t border-gray-light">
            <div
              className="mb-16"
              style={{
                backgroundColor: DARK,
                marginLeft: 'calc(50% - 50vw)',
                marginRight: 'calc(50% - 50vw)',
              }}
            >
              <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-20">
                <p
                  className="text-sm font-bold uppercase tracking-widest mb-3"
                  style={{ color: ACCENT }}
                >
                  Initial Research Findings · 1,475 survey responses
                </p>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-5 max-w-xl">
                  People love plants. They're just bad at keeping them alive.
                </h3>
                <p
                  className="text-lg leading-relaxed mb-10 max-w-2xl"
                  style={{ color: 'rgba(255,255,255,0.65)' }}
                >
                  During COVID, plant ownership exploded: a way to bring the outdoors in and make
                  homes feel more alive, with real health and environmental benefits attached. But
                  plants add life and color to a room only until they don't:{' '}
                  <strong className="text-white">
                    67% of millennials call themselves a "plant murderer,"
                  </strong>{' '}
                  killing an average of 7 or more houseplants.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                  <DarkCard large label="$17B" sub="Spent on plants in 2021" />
                  <DarkCard large label="$26.2B" sub="Projected by 2026, up 5% year over year" />
                </div>

                <p
                  className="text-xs font-bold uppercase tracking-widest mb-5"
                  style={{ color: 'rgba(255,255,255,0.4)' }}
                >
                  What the survey found
                </p>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <DarkCard large label="52%" sub="Killed 1-5 plants in the past two years" />
                  <DarkCard large label="90%" sub="Have a favorite plant they especially care about" />
                  <DarkCard
                    large
                    label="28%"
                    sub="Would spend $150+ on a smart device to protect their favorite plant"
                  />
                  <DarkCard large label="80%" sub="Already own a smart home device" />
                </div>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  Indoor Plant Care Survey, 1,475 respondents, closed October 2022.
                </p>
              </div>
            </div>

            <SectionHeading
              eyebrow="Process"
              title="From a consumer gadget to a funded commercial pivot"
            />

            <div className="mb-16">
              <h3 className="text-xl font-bold text-text-primary mb-3">First attempt: consumer</h3>
              <p className="text-tertiary text-lg mb-8 max-w-2xl">
                Built a working product and tested multiple approaches and markets, starting with
                consumer.
              </p>

              <p
                className="text-sm font-bold uppercase tracking-widest mb-3"
                style={{ color: ACCENT_DARK }}
              >
                V1: First pass
              </p>
              <p className="text-tertiary text-lg mb-8 max-w-2xl">
                The first version was a self-watering smart planter and companion app. The pot held a 2–3 month water supply, depending on the plant and its location, and watered automatically. In the app, you could scan a plant to identify it, get a location score for how well it would do where it was placed, track soil moisture and tank levels, and receive care recommendations. It connected to Alexa, Google Home, and Matter-certified devices, with custom light and color controls built into the pot itself.
              </p>
              <figure className="mb-8">
                <img
                  src={`${IMG}/pot.jpg`}
                  alt="The smart planter itself: a dark pot with soil sensor and a small embedded screen showing the plant's status"
                  className="w-full h-auto rounded-lg shadow-xl"
                />
                <figcaption className="mt-3 text-sm text-tertiary">
A small screen in the rim shows your plant's status, so you know how it's doing without opening an app.                </figcaption>
              </figure>

              <figure className="mb-8">
                <img
                  src={`${IMG}/room.jpg`}
                  alt="The Herba pot placed in a real living room, holding a monstera among the room's other furniture"
                  className="w-full h-auto rounded-lg shadow-xl"
                />
                <figcaption className="mt-3 text-sm text-tertiary">
                 Designed to fit into modern homes and offices, not labs. It looks like a sleek planter, and the sensors stay out of sight.
                </figcaption>
              </figure>

              <figure className="mb-8">
                <img
                  src={`${IMG}/consumer-app.jpg`}
                  alt="Consumer app: scanning a plant to identify it, then a detail screen with location score, tank fill, and soil moisture, plus smart-home integrations with Alexa, Google Home, and Matter"
                  className="w-full h-auto rounded-lg shadow-xl"
                />
                <figcaption className="mt-3 text-sm text-tertiary">
                  Scan a plant to identify it, then track its location score, tank fill, and soil
                  moisture in the companion app.
                </figcaption>
              </figure>

              <figure className="mb-8">
                <img
                  src={`${IMG}/light-controls.jpg`}
                  alt="Custom light settings screen with a brightness slider and a color wheel, shown against the pot glowing in a dark room"
                  className="w-full h-auto rounded-lg shadow-xl"
                />
                <figcaption className="mt-3 text-sm text-tertiary">
                  Custom brightness and color controls for the pot's built-in light, adjustable
                  right from the app.
                </figcaption>
              </figure>

              <figure>
                <img
                  src={`${IMG}/alert.jpg`}
                  alt="A glowing Herba pot at night beside a phone showing a time-sensitive alert: Leaf Erickson needs water"
                  className="w-full h-auto rounded-lg shadow-xl"
                />
                <figcaption className="mt-3 text-sm text-tertiary">
                  Alerts land the moment a plant needs attention, wherever the phone is.
                </figcaption>
              </figure>
            </div>

            <div
              style={{
                backgroundColor: DARK,
                marginLeft: 'calc(50% - 50vw)',
                marginRight: 'calc(50% - 50vw)',
              }}
            >
              <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-20">
                <p className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: ACCENT }}>
                  The pivot
                </p>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-10 max-w-2xl">
                  From a consumer flop to a funded commercial product
                </h3>

                <div className="flex flex-col lg:flex-row items-stretch gap-0 mb-8 lg:overflow-x-auto">
                  <PivotCard eyebrow="Started" title="Consumer launch">
                    Built a smart planter and app for home plant care. Nearly appeared on{' '}
                    <strong className="text-white">Shark Tank</strong>, but struggled to raise for
                    consumer hardware.
                  </PivotCard>
                  <PivotArrow caption="fundraising stalled" />
                  <PivotCard eyebrow="Turning point" title="Key research finding" highlight>
                    <strong className="text-white">60% of landscaping companies</strong> struggle to
                    hire and retain crew: the same sensors could solve a business problem, not just a
                    hobbyist one.
                  </PivotCard>
                  <PivotArrow caption="repackaged the same sensors" />
                  <PivotCard eyebrow="Action" title="Commercial pivot">
                    Re-targeted the same core hardware at commercial landscaping companies
                    and property managers.
                  </PivotCard>
                  <PivotArrow caption="raised from industry leaders" />
                  <PivotCard eyebrow="Result" title="$500K raised" highlight>
                    Funded by leaders in the commercial landscaping space, on hardware that had
                    already been built and tested.
                  </PivotCard>
                </div>

                <div
                  className="rounded-r-md border-l-4 p-6"
                  style={{ backgroundColor: 'rgba(0,200,83,0.06)', borderColor: ACCENT }}
                >
                  <p className="text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>
                    The pivot didn't require starting over: the{' '}
                    <strong style={{ color: ACCENT }}>
                      same patented sensor and irrigation system
                    </strong>{' '}
                    that couldn't find a consumer market found a business one, once the target buyer
                    changed from a homeowner to a landscaping company with a hiring problem.
                  </p>
                </div>
              </div>
            </div>

            <div
              style={{
                backgroundColor: DARK,
                marginLeft: 'calc(50% - 50vw)',
                marginRight: 'calc(50% - 50vw)',
              }}
            >
              <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-20">
                <p
                  className="text-sm font-bold uppercase tracking-widest mb-3"
                  style={{ color: ACCENT }}
                >
                  Beta feedback → PVT
                </p>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 max-w-xl">
                  What the field taught us that the lab couldn't
                </h3>
                <p
                  className="text-lg leading-relaxed mb-8 max-w-2xl"
                  style={{ color: 'rgba(255,255,255,0.65)' }}
                >
                  The 20-unit beta ran on real commercial properties, not a bench. That surfaced
                  problems no amount of indoor testing would have caught, and each one fed directly
                  into the next production iteration.
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8 items-start mb-8">
                  <figure className="lg:sticky lg:top-24">
                    <img
                      src={`${IMG}/beta-field-test.jpg`}
                      alt="An early beta sensor with an onboard screen taped inside a planter, wired to a laptop logging live sensor readings to a spreadsheet"
                      className="w-full h-auto rounded-lg"
                      style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                    />
                    <figcaption className="mt-3 text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
                      An early beta unit used Wi-Fi, logging live tank, light, and temperature
                      readings to Google Sheets.
                    </figcaption>
                  </figure>

                  <div className="flex flex-col">
                    <TimelineStep
                      eyebrow="Connectivity"
                      title="Wi-Fi doesn't reach across most commercial exterior spaces, and asking a crew to enter an SSID and password for every device doesn't scale past a handful of units."
                    >
                      <NextChange>
                        Explored cellular and satellite connectivity for outdoor deployments, and a
                        setup flow that doesn't require manual credentials per device.
                      </NextChange>
                    </TimelineStep>
                    <TimelineStep
                      eyebrow="Power & durability"
                      title="Solar power looked like a minor spec until the field showed how much placement and seasonal daylight actually determine whether a device stays powered."
                    >
                      <NextChange>
                        Adjustable panel placement, an easily swappable battery for recharging in the
                        field, and data transfer rates that scale down in winter months to conserve
                        power.
                      </NextChange>
                    </TimelineStep>
                    <TimelineStep
                      eyebrow="Identifying a device without a screen"
                      title="The onboard screen drew too much power to justify on units running off solar and battery outdoors long-term."
                    >
                      <NextChange>
                        Needed a new, low- or no-power way for a crew member to confirm which physical
                        device they're looking at, a simple LED indicator in place of a display.
                      </NextChange>
                    </TimelineStep>
                    <TimelineStep
                      eyebrow="Built to survive the ground"
                      title="Soil probes and the water solenoid both needed to be fully sealed for direct burial in real, sometimes rocky, commercial terrain, not just splash-resistant."
                    >
                      <NextChange>
                        Ruggedized, reinforced probes, an airtight solenoid housing, and hardware
                        connections redesigned specifically to hold up outdoors.
                      </NextChange>
                    </TimelineStep>
                    <TimelineStep
                      eyebrow="Smarter watering, not just scheduled watering"
                      title="Fixed watering thresholds don't account for real variation in soil, plant type, and season across a portfolio of properties."
                      last
                    >
                      <NextChange>
                        Use the same historical-tracking approach already powering refill-date
                        predictions to let AI adjust watering timing and amount per device over time.
                      </NextChange>
                    </TimelineStep>
                  </div>
                </div>

                <div
                  className="mt-9 rounded-r-md border-l-4 p-6"
                  style={{ backgroundColor: 'rgba(0,200,83,0.06)', borderColor: ACCENT }}
                >
                  <p className="text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>
                    None of this showed up in early testing because early testing didn't have{' '}
                    <strong style={{ color: ACCENT }}>real crews, real terrain, or a real winter</strong>.
                    The beta's job wasn't just to prove the product worked: it was to find out what
                    "works" actually requires once it leaves a controlled environment. I hired and
                    directed a team of{' '}
                    <strong style={{ color: ACCENT }}>8 contractors</strong> to take these
                    realizations from a punch list to production-ready hardware for PVT, coordinating
                    hardware, firmware, and manufacturing work in parallel.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-16">
              <div
                className="relative overflow-hidden"
                style={{
                  backgroundColor: DARK,
                  marginLeft: 'calc(50% - 50vw)',
                  marginRight: 'calc(50% - 50vw)',
                }}
              >
                <img
                  src={`${IMG}/hero-rings.jpg`}
                  alt=""
                  aria-hidden="true"
                  className="hidden md:block absolute top-0 right-0 h-full w-auto max-w-[45%] object-cover opacity-90 pointer-events-none select-none"
                />
                <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-20 relative">
                  <p
                    className="text-sm font-bold uppercase tracking-widest mb-3"
                    style={{ color: ACCENT }}
                  >
                    PVT
                  </p>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 max-w-xl">
                    What shipped
                  </h3>
                  <p
                    className="text-lg leading-relaxed mb-10 max-w-2xl"
                    style={{ color: 'rgba(255,255,255,0.65)' }}
                  >
                    From the field sensor to the dashboard a business owner actually checks.
                  </p>

                  <p className="text-white font-bold text-3xl sm:text-4xl leading-tight mb-14 max-w-md">
                    Intelligent. Automated. Landscaping.
                  </p>

                  <p className="text-white font-bold text-xl mb-5">Who uses Herba?</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12 max-w-2xl">
                    <DarkCard label="Landscaping companies" sub="Business owners" />
                    <DarkCard label="Crew" sub="Field and maintenance" />
                    <DarkCard label="Property managers" sub="HR, sustainability, facility managers" />
                  </div>

                  <p className="text-white font-bold text-xl mb-5">Why?</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                    <DarkCard label="Automation" sub="Automated watering reduces crew requirements" />
                    <DarkCard label="Healthier plants" sub="Patented technology with proven results" />
                    <DarkCard label="Monitoring" sub="Data and notifications on every device" />
                    <DarkCard label="Action" sub="Crews visit only when the data says a plant needs it" />
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-16">
              <h3 className="text-xl font-bold text-text-primary mb-3">Under the hood</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
                <ul className="space-y-3 text-tertiary text-lg">
                  {[
                    "Taught myself firmware development and wrote all the code, from soldering early sensor prototypes by hand to designing the solar-powered, in-ground sensor that shipped.",
                    'Designed and programmed the web app based on user research.',
                    'Invented and patented a sub-irrigation system controlled by a valve and sensor combination.',
                    "Also designed Herba's visual identity: logo, color system, and typography, alongside the product.",
                  ].map((item) => (
                    <li key={item} className="flex gap-3">
                      <span style={{ color: ACCENT_DARK }}>•</span>
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="grid grid-cols-2 gap-4">
                  <img
                    src="/images/herba3.jpg"
                    alt="Solar-powered in-ground sensor prototype, held by hand"
                    className="w-full h-full object-cover rounded-lg shadow-lg"
                  />
                  <img
                    src={`${IMG}/prototype-boards.jpg`}
                    alt="Early hand-soldered sensor board prototypes"
                    className="w-full h-full object-cover rounded-lg shadow-lg"
                  />
                </div>
              </div>

              <figure className="mt-10 max-w-2xl mx-auto">
                <img
                  src={`${IMG}/sensor-iterations.jpg`}
                  alt="Six sensor prototype iterations laid out side by side, showing different probe shapes, lengths, and housings tested before the final design"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
                <figcaption className="mt-3 text-sm text-tertiary text-center">
                  Sensor form factor went through several physical iterations (probe shape,
                  length, and housing) before landing on the final solar-powered design.
                </figcaption>
              </figure>

              <figure className="mt-10 max-w-md mx-auto">
                <img
                  src={`${IMG}/deployed-sensor.jpg`}
                  alt="The solar-powered in-ground sensor deployed in a real commercial planter bed, its panel visible among ornamental grasses and sedum"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </figure>

              <div className="mt-10 pt-8 border-t border-gray-light">
                <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-2">
                  US Patent No. 12,336,463 B1
                </p>
                <p className="text-tertiary text-lg italic mb-3 max-w-2xl">
                  "Wireless plant container with vacuum sealing for sub-irrigated self-watering
                  controlled by soil moisture and water level sensors"
                </p>
                <a
                  href="https://patents.google.com/patent/US12336463B1/en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block text-sm font-medium hover:underline mb-6"
                  style={{ color: ACCENT_DARK }}
                >
                  View Patent →
                </a>
                <figure className="max-w-2xl mb-8">
                  <img
                    src={`${IMG}/patent-fig1.png`}
                    alt="Patent Figure 1: a cross-section of the sealed planter showing the water reservoir, fill and drain valves, three soil moisture sensors at different depths, the water level sensor, and the wireless motherboard"
                    className="w-full h-auto rounded-lg border border-gray-light bg-white"
                  />
                  <figcaption className="mt-3 text-sm text-tertiary">
                    Fig. 1 from the granted patent: the sealed reservoir, fill and drain valves,
                    water level sensor, soil moisture sensors, and wireless motherboard, as filed
                    with the USPTO.
                  </figcaption>
                </figure>
                <figure className="max-w-2xl">
                  <img
                    src={`${IMG}/patent-fig4.png`}
                    alt="Patent Figure 4: a flowchart of the watering control logic, from checking soil moisture and reservoir water level through opening the air valve to start a watering cycle and recharging the battery"
                    className="w-full h-auto rounded-lg border border-gray-light bg-white"
                  />
                  <figcaption className="mt-3 text-sm text-tertiary">
                    Fig. 4 from the granted patent: the watering control logic, from checking soil
                    moisture and reservoir level to running a watering cycle and recharging the
                    battery, as filed with the USPTO.
                  </figcaption>
                </figure>
              </div>
            </div>

            <SectionHeading
              eyebrow="Software"
              title="From patented hardware to a product a business owner opens every day"
              intro="The same sensors still needed a web app: a way to turn raw tank, moisture, and battery readings into a dashboard, a fast setup flow, and a per-device drill-down for every property in a portfolio."
            />

            <div className="mb-16">
              <p
                className="text-xs font-bold uppercase tracking-widest mb-2"
                style={{ color: WIRE.accent }}
              >
                Site flow
              </p>
              <p className="font-bold text-text-primary mb-2">What the app actually covers</p>
              <p className="text-tertiary text-sm mb-6 max-w-md">
                Nine screens and states deep, from first sign-in to editing a single device's
                watering settings, the full surface area that had to be designed, not just the
                four highlight screens shown below.
              </p>

              <div className="overflow-x-auto">
                <div style={{ minWidth: 780 }}>
                  <ul className="herba-tree">
                    <li>
                      <TreeNode label="Sign in">
                        <WLine thin width="40%" center />
                        <WLine />
                        <WLine />
                        <WBtnRow center>
                          <WBtn width={70} />
                        </WBtnRow>
                      </TreeNode>
                      <ul>
                        <li>
                          <TreeNode label="Dashboard">
                            <WStatRow />
                            <WRow>
                              <WCircle />
                              <WPill />
                            </WRow>
                            <WRow>
                              <WCircle />
                              <WPill />
                            </WRow>
                            <WRow>
                              <WCircle />
                              <WPill />
                            </WRow>
                            <WBtnRow>
                              <WBtn />
                              <WBtn />
                            </WBtnRow>
                          </TreeNode>
                          <ul>
                            <li>
                              <TreeNode label="Device detail">
                                <WLine />
                                <WGraph />
                                <WMap />
                                <WLine thin />
                                <WLine thin width="70%" />
                              </TreeNode>
                              <ul>
                                <li>
                                  <TreeNode label="Device settings">
                                    <WRow>
                                      <WCircle />
                                      <WPill />
                                      <WPill maxWidth={26} />
                                    </WRow>
                                    <WRow>
                                      <WCircle />
                                      <WPill />
                                      <WPill maxWidth={26} />
                                    </WRow>
                                    <WRow>
                                      <WCircle />
                                      <WPill />
                                      <WPill maxWidth={26} />
                                    </WRow>
                                  </TreeNode>
                                </li>
                              </ul>
                            </li>

                            <li>
                              <TreeNode label="Locations">
                                <div className="flex gap-1.5">
                                  <div className="flex-1">
                                    <WRow>
                                      <WCircle />
                                      <WLine thin width="100%" />
                                    </WRow>
                                    <WMap />
                                  </div>
                                  <div className="flex-1">
                                    <WRow>
                                      <WCircle />
                                      <WLine thin width="100%" />
                                    </WRow>
                                    <WMap />
                                  </div>
                                </div>
                              </TreeNode>
                              <ul>
                                <li>
                                  <TreeNode label="Add / edit location">
                                    <WModal>
                                      <WRow>
                                        <WPill />
                                        <WBtn width={40} />
                                      </WRow>
                                      <WLine thin />
                                      <WLine thin />
                                    </WModal>
                                  </TreeNode>
                                </li>
                              </ul>
                            </li>

                            <li>
                              <TreeNode label="Users">
                                <WRow>
                                  <WLine thin width="100%" />
                                  <span style={{ width: 24 }}>
                                    <WLine thin width="100%" />
                                  </span>
                                </WRow>
                                <WRow>
                                  <WLine thin width="100%" />
                                  <span style={{ width: 24 }}>
                                    <WLine thin width="100%" />
                                  </span>
                                </WRow>
                                <WRow>
                                  <WLine thin width="100%" />
                                  <span style={{ width: 24 }}>
                                    <WLine thin width="100%" />
                                  </span>
                                </WRow>
                                <WBtnRow>
                                  <WBtn />
                                </WBtnRow>
                              </TreeNode>
                              <ul>
                                <li>
                                  <TreeNode label="Add user">
                                    <WModal>
                                      <WRow>
                                        <WPill />
                                        <WPill />
                                      </WRow>
                                      <WBtnRow>
                                        <WBtn width={34} />
                                        <WBtn />
                                      </WBtnRow>
                                    </WModal>
                                  </TreeNode>
                                </li>
                              </ul>
                            </li>

                            <li>
                              <TreeNode label="Notifications">
                                <WRow>
                                  <WCircle />
                                  <WLine thin width="100%" />
                                </WRow>
                                <WLine thin width="80%" />
                                <div className="mb-1.5" />
                                <WRow>
                                  <WCircle />
                                  <WLine thin width="100%" />
                                </WRow>
                                <WLine thin width="80%" />
                              </TreeNode>
                            </li>
                          </ul>
                        </li>
                      </ul>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-text-primary mb-2">Product: what shipped</h3>
              <p className="text-tertiary mb-6 max-w-2xl text-lg">
                Four screens carry the whole job: see everything at a glance, add a device in under a
                minute, drill into one location, and let AI read a photo from the field.
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start mb-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-3">Dashboard</p>
                  <Shot
                    src="/images/herba1.jpg"
                    alt="Dashboard showing devices online, tank fill, and battery level across every location"
                    caption="A high-level view of every device and location: devices online, water levels, and battery health across an entire portfolio of properties at a glance."
                  />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-3">
                    Detail view
                  </p>
                  <PhoneShot
                    src={`${IMG}/detail-view.png`}
                    alt="Detail view for a single device showing water level, refill date, tank status, and battery"
                  />
                  <p className="text-tertiary text-sm leading-relaxed mt-3">
                    Drilling into a single device surfaces water level, estimated refill date,
                    tank status, battery, and an events log, giving business owners visibility
                    into potential lapses before they become a problem for the plant.
                  </p>
                  <p className="text-tertiary text-sm leading-relaxed mt-3">
                    Location comes straight from the device's{' '}
                    <strong style={{ color: ACCENT_DARK }}>GPS</strong>, shown on an embedded map.
                    Notes can easily be added in the field, and "Edit settings"
                    deep-links straight into that device's row in Device Settings instead of a
                    generic settings list.
                  </p>
                </div>
              </div>
            </div>

            <div
              className="mb-16"
              style={{
                backgroundColor: DARK,
                marginLeft: 'calc(50% - 50vw)',
                marginRight: 'calc(50% - 50vw)',
              }}
            >
              <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-20">
                <p
                  className="text-xs font-bold uppercase tracking-widest mb-2"
                  style={{ color: ACCENT }}
                >
                  From a snapshot to a prediction
                </p>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
                  How tank fill % becomes a refill date
                </h3>
                <p className="text-sm leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.65)' }}>
                  The dashboard shows tank fill as a single percentage. On its own, "46%" doesn't
                  tell a business owner whether that's urgent or fine for another three weeks. The
                  historical tracking underneath it does.
                </p>

                <div className="flex flex-col">
                  <TimelineStep eyebrow="Logged" title="Every wake cycle records a tank fill reading">
                    A single snapshot, timestamped, same as soil moisture and battery.
                  </TimelineStep>
                  <TimelineStep
                    eyebrow="Accumulated"
                    title="Readings build a consumption history per device"
                  >
                    Days of readings show how fast that specific planter actually drains, which
                    varies by plant, season, and watering frequency, not just tank size.
                  </TimelineStep>
                  <TimelineStep eyebrow="Modeled" title="Depletion rate is projected forward">
                    The trend in the historical data, not the current % alone, predicts when the
                    tank will actually hit empty.
                  </TimelineStep>
                  <TimelineStep
                    eyebrow="Shown"
                    title="Estimated refill date appears next to the raw %"
                    highlight
                    last
                  >
                    Turns a number that needs interpreting into a date that needs no math from the
                    business owner.
                    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 mt-3">
                      <div
                        className="rounded-lg p-4"
                        style={{ backgroundColor: CARD_BG, border: '1px solid rgba(255,255,255,0.12)' }}
                      >
                        <p
                          className="text-[10px] uppercase tracking-widest mb-1"
                          style={{ color: 'rgba(255,255,255,0.4)' }}
                        >
                          Raw reading
                        </p>
                        <p className="text-white font-bold text-xl">46%</p>
                      </div>
                      <span style={{ color: 'rgba(255,255,255,0.4)' }}>→</span>
                      <div
                        className="rounded-lg p-4"
                        style={{ backgroundColor: 'rgba(0,200,83,0.07)', border: `1px solid ${ACCENT}` }}
                      >
                        <p
                          className="text-[10px] uppercase tracking-widest mb-1"
                          style={{ color: 'rgba(255,255,255,0.4)' }}
                        >
                          Shown on dashboard
                        </p>
                        <p className="font-bold text-xl" style={{ color: ACCENT }}>
                          Est. refill: 9/25
                        </p>
                      </div>
                    </div>
                  </TimelineStep>
                </div>

                <div
                  className="mt-9 rounded-r-md border-l-4 p-6"
                  style={{ backgroundColor: 'rgba(0,200,83,0.06)', borderColor: ACCENT }}
                >
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>
                    A single reading can't tell you that:{' '}
                    <strong style={{ color: ACCENT }}>46% today</strong> could mean two days left or
                    three weeks, depending on that device's actual drain rate. Only the history
                    does. This is what let "Action: Adjust scheduling based on real data" become a
                    real feature instead of a tagline: crews can plan refill routes around{' '}
                    <strong style={{ color: ACCENT }}>predicted</strong> empty dates instead of
                    reacting to alerts after the tank's already dry.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-16 mb-16">
              {/* Row: Simple setup flow — header above, screenshot + wireframe as two columns, copy below */}
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-4">
                  Simple setup flow
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mb-6">
                  <PhoneShot
                    src={`${IMG}/setup-modal.png`}
                    alt="Add a Device modal with a QR code scanner and manual device ID entry"
                  />
                  <div
                    className="rounded-lg p-4"
                    style={{ backgroundColor: WIRE.card }}
                  >
                    <p
                      className="text-[10px] font-bold uppercase tracking-widest text-center mb-3"
                      style={{ color: WIRE.accent }}
                    >
                      Setup flow
                    </p>
                    <FlowBox label={'"Add a Device" modal opens'} />
                    <FlowArrow />
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="flex-1">
                        <FlowBox dashed label="Scan QR code" />
                      </div>
                      <div className="flex-1">
                        <FlowBox dashed label="Enter Device ID manually" />
                      </div>
                    </div>
                    <FlowArrow />
                    <FlowBox label={'"Continue" enables'} sub="Disabled until an ID is present" />
                    <FlowArrow />
                    <FlowBox label="Device appears on Dashboard" />
                  </div>
                </div>
                <p className="text-tertiary text-lg leading-relaxed mb-4">
                  Users scan a QR code on the device to add it to their dashboard, with manual
                  device ID entry as a fallback. Built for mobile-first setup so a crew member
                  can add a device on-site in under a minute.
                </p>
                <p className="text-tertiary text-lg leading-relaxed">
                  The QR code opens a{' '}
                  <strong style={{ color: ACCENT_DARK }}>device-specific deep-linked URL</strong>.
                  If someone's already signed in from scanning an earlier device that same
                  session, they don't have to log back in for every new device they add.
                </p>
              </div>

              {/* Row: Admin: user management */}
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-4">
                  Admin: user management
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-[1.6fr_1fr] gap-6 items-start mb-6">
                  <Shot
                    src={`${IMG}/users-page.png`}
                    alt="Users table listing each team member's name, email, and access level as a colored pill (dark blue for Admin, light blue for Editor, light gray for Viewer), with a Remove User link on every row except Kacey's"
                    caption="Access level shows as a colored pill, darker for higher permissions, so the column is scannable at a glance. Notice Kacey's row has no 'Remove User' link: admins can't remove themselves."
                  />
                  {/* Cropped modal with transparent rounded corners, so the shadow is a
                      drop-shadow filter that follows its shape instead of Shot's boxed frame. */}
                  <figure className="flex flex-col h-full">
                    <img
                      src={`${IMG}/add-user-modal.png`}
                      alt="Add User modal with an email field and Admin, Editor, and Viewer radio options, each with a short description of what that level can do"
                      className="w-full h-auto block"
                      style={{ filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.12))' }}
                    />
                    <figcaption className="mt-3 text-sm text-tertiary">
                      Admin: full access. Editor: can add and configure devices, but not users or
                      settings. Viewer: view-only, plus notes and photos.
                    </figcaption>
                  </figure>
                </div>
                <p className="text-tertiary text-lg leading-relaxed">
                  Not everyone touching a Herba dashboard should be able to change it. Field crews
                  need to see tank levels and add notes from the ground; installers need to add
                  devices on-site; only admins should manage users or locations. Three access
                  levels split those lines: Admin gets full control, Editor can add and configure
                  devices, Viewer gets visibility without the ability to change anything.
                </p>
              </div>

              {/* Row: Multi-location management */}
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-4">
                  Multi-location management
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-[1.6fr_1fr] gap-6 items-start mb-6">
                  <Shot
                    src={`${IMG}/locations-page.png`}
                    alt="Locations page listing PO Tournesol and Red Wood, each showing device count, status, an embedded map, and a View Dashboard link"
                  />
                  <PhoneShot
                    src={`${IMG}/add-location-modal.png`}
                    alt="Add Location modal with location name and address fields, an Add Location button, and a list of existing locations showing each one's device count"
                  />
                </div>
                <div
                  className="rounded-lg p-4 mb-6 max-w-md mx-auto"
                  style={{ backgroundColor: WIRE.card }}
                >
                  <p
                    className="text-[10px] font-bold uppercase tracking-widest text-center mb-3"
                    style={{ color: WIRE.accent }}
                  >
                    Multi-location setup
                  </p>
                  <FlowBox label={'Admin opens "Add / Edit Locations"'} />
                  <FlowArrow />
                  <FlowBox
                    label={'Enters location name + address, clicks "Add Location"'}
                    sub="Disabled until a name is entered"
                  />
                  <FlowArrow />
                  <FlowBox label={'Location created, appears in "Edit Existing Locations" with 0 devices'} />
                  <FlowArrow />
                  <FlowBox
                    dashed
                    label={"Separately: assign a device to it from Device Settings' Location dropdown"}
                  />
                  <FlowArrow />
                  <FlowBox label={'Location shows device count, status, map pins, and "View Dashboard," filterable from the Dashboard'} />
                </div>
                <p className="text-tertiary text-lg leading-relaxed">
                  A landscaping company isn't managing one planter. It's managing a portfolio of
                  properties, each with its own devices, its own issues, and its own crew route.
                  The Locations page groups devices by property, showing device count and status
                  at a glance, so a business owner can see "Red Wood" as one unit with 3 devices
                  and no issues, instead of scrolling a flat list of every sensor they own.
                </p>
              </div>

              {/* Row: AI monitoring — header above, screenshot + wireframe as two columns, copy below */}
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-4">
                  AI monitoring
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mb-6">
                  <PhoneShot
                    src={`${IMG}/ai-monitoring.jpg`}
                    alt="AI photo analysis of a planter, identifying the species and confirming plant health with 95% confidence"
                  />
                  <div className="rounded-lg p-4" style={{ backgroundColor: WIRE.card }}>
                    <p
                      className="text-[10px] font-bold uppercase tracking-widest text-center mb-3"
                      style={{ color: WIRE.accent }}
                    >
                      AI monitoring flow
                    </p>
                    <FlowBox label="Crew member takes a photo" />
                    <FlowArrow />
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="flex-1">
                        <FlowBox dashed label="AI image analysis" />
                      </div>
                      <div className="flex-1">
                        <FlowBox dashed label="Device's sensor history" />
                      </div>
                    </div>
                    <FlowArrow />
                    <FlowBox highlight label="Combined health analysis" sub="image + moisture, watering trend" />
                    <FlowArrow />
                    <FlowBox end label="Diagnosis + confidence, recommendation" />
                  </div>
                </div>
                <p className="text-tertiary text-lg leading-relaxed mb-4">
                  AI photo recognition paired with sensor data gives insight into plant health
                  issues and provides care recommendations, turning a photo from the field into
                  a confidence-scored diagnosis and next step.
                </p>
                <p className="text-tertiary text-lg leading-relaxed">
                  It's not purely photo-based: the model{' '}
                  <strong style={{ color: ACCENT_DARK }}>
                    cross-references sensor data alongside the photo
                  </strong>
                  , so a healthy-looking plant sitting on a draining tank still gets flagged
                  before it's visibly stressed.
                </p>
              </div>

              {/* Row: Notifications */}
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-4">
                  Notifications
                </p>
                <p className="text-tertiary text-lg leading-relaxed mb-4">
                  A device doesn't just log a problem. It surfaces it three ways at once: a red
                  dot on its dashboard card for an at-a-glance signal, a full alert in the
                  notifications panel (bell icon, top right), and an entry in that device's own
                  events log.
                </p>
                <p className="text-tertiary text-lg leading-relaxed mb-6">
                  Every alert follows the same two-line pattern: a plain-language header naming
                  the device and the issue, and a subcopy line explaining what it means and what
                  to do about it.
                </p>

                <div
                  className="rounded-lg p-4 mb-8 max-w-md mx-auto"
                  style={{ backgroundColor: WIRE.card }}
                >
                  <p
                    className="text-[10px] font-bold uppercase tracking-widest text-center mb-3"
                    style={{ color: WIRE.accent }}
                  >
                    Notifications flow
                  </p>
                  <FlowBox
                    label="Device condition changes"
                    sub="Water level, battery, tank seal, or connection status"
                  />
                  <FlowArrow />
                  <FlowBox label="Notification created: header + subcopy, device name included" />
                  <FlowArrow />
                  <FlowBox
                    dashed
                    label="Surfaces in two places at once:"
                    sub={
                      <>
                        Bell icon panel (all devices, chronological)
                        <br />
                        That device's own events log
                      </>
                    }
                  />
                  <FlowArrow />
                  <FlowBox label="Red dot appears on the device's Dashboard card" />
                </div>

                <div
                  className="rounded-lg p-4 mb-3"
                  style={{ backgroundColor: WIRE.card }}
                >
                  <p
                    className="text-[10px] font-bold uppercase tracking-widest text-center mb-3"
                    style={{ color: WIRE.accent }}
                  >
                    Alert types
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full" style={{ minWidth: 640, borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          {['Type', 'Severity', 'Header pattern', 'Subcopy pattern'].map((h) => (
                            <th
                              key={h}
                              className="text-left text-[10px] font-bold uppercase tracking-widest px-3 py-2"
                              style={{ color: WIRE.muted, borderBottom: `1px solid ${WIRE.border}` }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {ALERT_TYPES.map((row) => (
                          <tr key={row.type}>
                            <td
                              className="px-3 py-2.5 text-[10px] align-top font-mono"
                              style={{ borderBottom: `1px solid ${WIRE.border}`, color: WIRE.ink }}
                            >
                              <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                                <span
                                  className="w-2 h-2 rounded-full flex-shrink-0"
                                  style={{
                                    backgroundColor: row.severity === 'warning' ? WIRE.warn : WIRE.accent,
                                  }}
                                />
                                {row.type}
                              </span>
                            </td>
                            <td
                              className="px-3 py-2.5 text-[10px] align-top capitalize"
                              style={{ borderBottom: `1px solid ${WIRE.border}`, color: WIRE.field }}
                            >
                              {row.severity}
                            </td>
                            <td
                              className="px-3 py-2.5 text-[11px] font-bold align-top"
                              style={{ borderBottom: `1px solid ${WIRE.border}`, color: WIRE.ink }}
                            >
                              {row.header}
                            </td>
                            <td
                              className="px-3 py-2.5 text-[10px] align-top"
                              style={{ borderBottom: `1px solid ${WIRE.border}`, color: WIRE.field }}
                            >
                              {row.subcopy}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <p className="text-tertiary text-sm leading-relaxed">
                  Every alert but one is a warning. The exception is tank refilled, which
                  confirms watering can resume rather than flagging a problem. Naming the device
                  and the fix in plain language, right in the header, means a crew member never
                  has to open the app to understand what's wrong.
                </p>
              </div>
            </div>

          </section>

          {/* Results */}
          <section className="py-16 sm:py-24 border-t border-gray-light">
            <SectionHeading eyebrow="Results" title="Launched a 20-unit beta with a 95% success rate" />
            <p className="text-tertiary text-lg mb-10 max-w-2xl">
              After incorporating what the earlier beta taught us (the connectivity, power, and
              durability fixes described above), this second 20-unit beta ran the updated hardware
              on real commercial properties. Before-and-after photos from the beta show plants that
              had been struggling (sparse, wilting, under-filled beds) filled out and thriving
              within the test window.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <BeforeAfterPair
                before={`${IMG}/before-1.jpg`}
                after={`${IMG}/after-1.jpg`}
                alt="A potted schefflera"
              />
              <BeforeAfterPair
                before={`${IMG}/before-2.jpg`}
                after={`${IMG}/after-2.jpg`}
                alt="A concrete planter bed"
              />
            </div>
          </section>

          {/* Outcome */}
          <section className="border-t border-gray-light">
            <div
              style={{ backgroundColor: DARK, marginLeft: 'calc(50% - 50vw)', marginRight: 'calc(50% - 50vw)' }}
            >
              <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-20">
                <p className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: ACCENT }}>
                  Outcome
                </p>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-10 max-w-2xl">
                  Measured after launch
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <DarkCard large label="$500K" sub="Raised in pre-seed funding" />
                  <DarkCard large label="Launched" sub="A successful beta with real hardware in the field" />
                  <DarkCard
                    large
                    label="Patent"
                    sub="Approved; product fully protected (US Patent No. 12,336,463 B1)"
                  />
                  <DarkCard large label="3 years" sub="Of R&D working with leading scientists" />
                  <DarkCard large label="Partnerships" sub="With the largest manufacturer in the space" />
                </div>
              </div>
            </div>
          </section>

          {/* Where Herba stands now */}
          <section className="py-16 sm:py-24 border-t border-gray-light">
            <h2 className="text-2xl sm:text-3xl font-bold text-text-primary mb-6 max-w-2xl">
              Where Herba stands now
            </h2>
            <p className="text-tertiary text-lg max-w-3xl">
              Herba ceased operations in October 2025. New tariffs on imported hardware
              components made the unit economics of continued manufacturing untenable,
              cutting off the path to scale past the beta.
            </p>
          </section>

          <OtherProjects current="herba" />
        </div>
      </div>
      <Footer />
    </CaseStudyGate>
  );
};

export default Herba;
